import { inject } from '@angular/core';
import { Router, CanActivateFn } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import {
  CrowdctrlToastService,
  OrderService,
  TenantSelectionService,
  extractErrorMessage,
} from '@crowdctrl/shared';
/**
 * Guard for order-related routes (order detail, receipt, cancel, move).
 * Verifies the order exists before allowing navigation.
 * If the order does not exist: shows error toast, redirects to my-orders, blocks navigation.
 */
export const orderDetailGuard: CanActivateFn = async (route) => {
  const router = inject(Router);
  const orderService = inject(OrderService);
  const tenantSelectionService = inject(TenantSelectionService);
  const toastService = inject(CrowdctrlToastService);

  // Params: parent has id, sessionId; this route has orderId
  const workspaceId = route.parent?.paramMap.get('id') ?? null;
  const sessionId = route.parent?.paramMap.get('sessionId') ?? null;
  const orderId = route.paramMap.get('orderId') ?? null;

  if (!workspaceId || !sessionId || !orderId) {
    router.navigate(['/workspaces']);
    return false;
  }

  const myOrdersUrl = ['/workspaces', workspaceId, 'sessions', sessionId, 'my-orders'];

  try {
    const result = await firstValueFrom(orderService.getOrder(workspaceId, sessionId, orderId));

    if (result?.order) {
      return true;
    }

    await toastService.show({
      message: 'Order not found.',
      duration: 3000,
      color: 'danger',
    });
    router.navigate(myOrdersUrl, { replaceUrl: true });
    return false;
  } catch (error) {
    const errorMsg = extractErrorMessage(error) || 'Failed to load order.';
    await toastService.show({
      message: errorMsg,
      duration: 3000,
      color: 'danger',
    });
    router.navigate(myOrdersUrl, { replaceUrl: true });
    return false;
  }
};
